import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import DialogHost from "./components/dialogs/DialogHost";

import Header from "./components/Header";
import Hero from "./components/Hero";
import WhyUs from "./components/WhyUs";
import Services from "./components/Services";
import LoanTypes from "./components/LoanTypes";
import MortgageCalculator from "./components/MortgageCalculator";
import Process from "./components/Process";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";

import { AuthProvider } from "./portal/AuthContext";
import { OwnerModeProvider } from "./portal/OwnerModeContext";
import LoginPage from "./portal/LoginPage";
import LoginRedirect from "./portal/LoginRedirect";
import AuthCallback from "./portal/AuthCallback";
import PortalLayout from "./portal/PortalLayout";
import RequireTenantAdmin from "./portal/RequireTenantAdmin";
import { LanguageProvider } from "./portal/LanguageContext";
import DashboardPage from "./portal/DashboardPage";
import LeadsPage from "./portal/LeadsPage";
import LeadDetailPage from "./portal/LeadDetailPage";
import PipelinePage from "./portal/PipelinePage";
import TasksPage from "./portal/TasksPage";
import AccountPage from "./portal/AccountPage";
import NotificationsPage from "./portal/NotificationsPage";
import MyNotificationsPage from "./portal/MyNotificationsPage";
import AppointmentsPage from "./portal/AppointmentsPage";
import AudreyProPage from "./portal/AudreyProPage";
import GuidelinesBrowserPage from "./portal/GuidelinesBrowserPage";
import CalculatorsPage from "./portal/CalculatorsPage";
import IncomeCalculatorPage from "./portal/IncomeCalculatorPage";
import DocumentsPage from "./portal/DocumentsPage";
import MarketingPage from "./portal/MarketingPage";
import EmailAnalyticsPage from "./portal/EmailAnalyticsPage";
import CommissionsPage from "./portal/CommissionsPage";
import BorrowerPortalPage from "./portal/BorrowerPortalPage";
import RealtorPortalPage from "./portal/RealtorPortalPage";
import PartyPortalPage from "./portal/PartyPortalPage";
import InviteAcceptPage from "./portal/InviteAcceptPage";
import VerifyEmailPage from "./portal/VerifyEmailPage";
import CertificateVerifyPage from "./portal/CertificateVerifyPage";
import ForgotPasswordPage from "./portal/ForgotPasswordPage";
import ResetPasswordPage from "./portal/ResetPasswordPage";
import LoanFilesPage from "./portal/LoanFilesPage";
import LoanFileDetailPage from "./portal/LoanFileDetailPage";
import CompliancePage from "./portal/CompliancePage";
import MCRPage from "./portal/MCRPage";
import LoanStatusPublicPage from "./portal/LoanStatusPublicPage";
import TransactionStatusPublicPage from "./portal/TransactionStatusPublicPage";
import ESignPage from "./portal/ESignPage";
import BorrowerUploadPage from "./portal/BorrowerUploadPage";
import RealEstateHubPage from "./portal/RealEstateHubPage";
import { DealsListPage, UnifiedDealPage } from "./portal/DealsPages";
import { AgentSitePublicPage, LandingPagePublic } from "./portal/PublicMarketingPages";
import LeadFormPublicPage from "./portal/LeadFormPublicPage";
import BrandingSettingsPage from "./portal/BrandingSettingsPage";
import SuperAdminPage from "./portal/SuperAdminPage";
import RevenueDashboardPage from "./portal/RevenueDashboardPage";
import FeedbackPage from "./portal/super/FeedbackPage";
import ReferralsPage from "./portal/ReferralsPage";
import TransactionDetailPage from "./portal/TransactionDetailPage";
import BillingSettingsPage from "./portal/BillingSettingsPage";
import BrokerCompliancePage from "./portal/BrokerCompliancePage";
import AttorneyReviewPage from "./portal/AttorneyReviewPage";
import FeeWorksheetPage from "./portal/FeeWorksheetPage";
import BorrowerFeeApprovalPage from "./portal/BorrowerFeeApprovalPage";
import BorrowerDealPortal from "./portal/BorrowerDealPortal";
import ExternalDealPage from "./portal/ExternalDealPage";
import UnifiedContactsPage from "./portal/UnifiedContactsPage";
import MergeQueuePage from "./portal/MergeQueuePage";
import ContactProfilePage from "./portal/ContactProfilePage";
import AutomationsPage from "./portal/AutomationsPage";
import InboxPage from "./portal/InboxPage";
import NotFoundPage, { PortalNotFound } from "./portal/NotFoundPage";
import PartnersAdminPage from "./portal/PartnersAdminPage";
import PartnerPortalPage from "./portal/PartnerPortalPage";
import OrgSettingsPage from "./portal/OrgSettingsPage";
import EnterprisePage from "./portal/EnterprisePage";
import TrainingCenterPage from "./portal/TrainingCenterPage";
import MyLoanOfficerHome from "./MyLoanOfficerHome";
import TenantSitePage from "./TenantSitePage";

const MarketingSite = () => {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap";
    document.head.appendChild(link);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("in");
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative">
      <Header />
      <main>
        <Hero />
        <WhyUs />
        <Services />
        <LoanTypes />
        <MortgageCalculator />
        <Process />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
          <OwnerModeProvider>
            <Routes>
              <Route path="/" element={<MyLoanOfficerHome />} />
              <Route path="/topchoicelending" element={<MarketingSite />} />
              <Route path="/site/:slug" element={<TenantSitePage />} />
              <Route path="/login" element={<LoginRedirect />} />
              <Route path="/portal/login" element={<LoginPage />} />
              <Route path="/portal/login/:slug" element={<LoginPage />} />
              <Route path="/portal/signup" element={<LoginPage />} />
              <Route path="/portal/invite" element={<InviteAcceptPage />} />
              <Route path="/portal/verify-email" element={<VerifyEmailPage />} />
              <Route path="/portal/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/portal/reset-password" element={<ResetPasswordPage />} />
              <Route path="/portal/auth/callback" element={<AuthCallback />} />
              <Route path="/borrower" element={<BorrowerPortalPage />} />
              <Route path="/realtor" element={<RealtorPortalPage />} />
              <Route path="/party" element={<PartyPortalPage />} />
              <Route path="/deal/:dealId" element={<ExternalDealPage />} />
              <Route path="/loan/:shareToken" element={<LoanStatusPublicPage />} />
              <Route path="/tx/:shareToken" element={<TransactionStatusPublicPage />} />
              <Route path="/e-sign/:token" element={<ESignPage />} />
              <Route path="/upload/:token" element={<BorrowerUploadPage />} />
              <Route path="/attorney/review/:token" element={<AttorneyReviewPage />} />
              <Route path="/borrower/fee-worksheet/:token" element={<BorrowerFeeApprovalPage />} />
              <Route path="/borrower/deal/:token" element={<BorrowerDealPortal />} />
              <Route path="/agent/:slug" element={<AgentSitePublicPage />} />
              <Route path="/lp/:slug" element={<LandingPagePublic />} />
              <Route path="/f/:slug" element={<LeadFormPublicPage />} />
              <Route path="/partner" element={<PartnerPortalPage />} />
              <Route path="/verify-certificate" element={<CertificateVerifyPage />} />
              <Route path="/verify-certificate/:code" element={<CertificateVerifyPage />} />
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="leads/:leadId" element={<LeadDetailPage />} />
                <Route path="pipeline" element={<PipelinePage />} />
                <Route path="contacts" element={<UnifiedContactsPage />} />
                <Route path="contacts/legacy" element={<Navigate to="/portal/contacts" replace />} />
                <Route path="contacts/review" element={<MergeQueuePage />} />
                <Route path="contacts/:contactId" element={<ContactProfilePage />} />
                {/* Ax 08: workspace routes retired — redirects kept one release (remove after 2026-09-23) */}
                <Route path="mortgage-workspace" element={<Navigate to="/portal/pipeline" replace />} />
                <Route path="re-workspace" element={<Navigate to="/portal/pipeline" replace />} />
                <Route path="automations" element={<AutomationsPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="partners" element={<PartnersAdminPage />} />
                <Route path="training" element={<TrainingCenterPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="my-notifications" element={<MyNotificationsPage />} />
                <Route path="calendar" element={<AppointmentsPage />} />
                <Route path="ai" element={<AudreyProPage />} />
                <Route path="ai/guidelines" element={<GuidelinesBrowserPage />} />
                <Route path="calculators" element={<CalculatorsPage />} />
                <Route path="income" element={<IncomeCalculatorPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="email-analytics" element={<EmailAnalyticsPage />} />
                <Route path="commissions" element={<CommissionsPage />} />
                <Route path="loans" element={<LoanFilesPage />} />
                <Route path="loans/:loanId" element={<LoanFileDetailPage />} />
                <Route path="compliance" element={<CompliancePage />} />
                <Route path="compliance/kit" element={<BrokerCompliancePage />} />
                <Route path="fee-worksheet" element={<FeeWorksheetPage />} />
                <Route path="mcr" element={<MCRPage />} />
                <Route path="real-estate" element={<RealEstateHubPage />} />
                <Route path="deals" element={<DealsListPage />} />
                <Route path="deals/:dealId" element={<UnifiedDealPage />} />
                {/* Admin-gated pages: nav hides them, the guard blocks the URL (ticket 98 N3/N7) */}
                <Route element={<RequireTenantAdmin />}>
                  <Route path="settings/org" element={<OrgSettingsPage />} />
                  <Route path="settings/branding" element={<BrandingSettingsPage />} />
                  <Route path="enterprise" element={<EnterprisePage />} />
                  <Route path="settings/billing" element={<BillingSettingsPage />} />
                </Route>
                <Route path="super-admin" element={<SuperAdminPage />} />
                <Route path="super-admin/revenue" element={<RevenueDashboardPage />} />
                <Route path="super-admin/feedback" element={<FeedbackPage />} />
                <Route path="refer" element={<ReferralsPage />} />
                <Route path="real-estate/transactions/:id" element={<TransactionDetailPage />} />
                <Route path="*" element={<PortalNotFound />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          <Toaster />
          <SonnerToaster />
          <DialogHost />
          </OwnerModeProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
